import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Combobox as ComboboxRoot,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/combobox'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { XIcon } from 'lucide-react'

type Option = { value: string; label: string }

const CREATE_PREFIX = '__create__'

export function MultiComboboxPopover(props: {
  id?: string
  value: string[]
  onChange: (value: string[], selectedValue: string | undefined) => void
  options: Option[]
  placeholder?: string
  searchPlaceholder: string
  commandEmpty: string
  creatable?: boolean
  createLabel?: (value: string) => string
  onCreateNew?: (value: string) => void
  variant?: 'default' | 'outline'
  className?: string
  isError?: boolean
}) {
  const [searchValue, setSearchValue] = useState('')

  const optionIds = new Set(props.options.map((o) => o.value))
  const baseOptions = props.creatable
    ? [...props.options, ...props.value.filter((v) => v && !optionIds.has(v)).map((v) => ({ value: v, label: v }))]
    : props.options

  const hasExactMatch = baseOptions.some(
    (o) => o.value.toLowerCase() === searchValue.toLowerCase() || o.label.toLowerCase() === searchValue.toLowerCase(),
  )
  const showCreateOption = props.creatable && searchValue.trim() && !hasExactMatch

  const allItems = !showCreateOption
    ? baseOptions
    : [
        ...baseOptions,
        {
          value: `${CREATE_PREFIX}${searchValue}`,
          label: props.createLabel ? props.createLabel(searchValue) : `Create "${searchValue}"`,
        },
      ]

  const selectedItems = baseOptions.filter((o) => props.value.includes(o.value))

  const labelByValue = new Map(baseOptions.map((o) => [o.value, o.label]))

  return (
    <ComboboxRoot
      // Non-modal: a modal popup marks the host form/dialog inert, hiding the surrounding fields and the submit button
      // from the a11y tree and from pointer events until dismissed. Outside-click / Escape still close it.
      modal={false}
      multiple
      items={allItems}
      value={selectedItems}
      onValueChange={(newItems: Option[]) => {
        const createItem = newItems.find((item) => item.value.startsWith(CREATE_PREFIX))
        if (createItem && props.onCreateNew) {
          const newValue = createItem.value.slice(CREATE_PREFIX.length)
          props.onCreateNew(newValue)
          if (props.creatable) setSearchValue('')
          return
        }

        const newValues = newItems.map((item) => {
          if (item.value.startsWith(CREATE_PREFIX)) {
            return item.value.slice(CREATE_PREFIX.length)
          }
          return item.value
        })
        const addedValue = newValues.find((v) => !props.value.includes(v))
        props.onChange(newValues, addedValue)
        if (addedValue && props.creatable) setSearchValue('')
      }}
      onInputValueChange={(inputValue) => setSearchValue(inputValue)}
      isItemEqualToValue={(a: Option, b: Option) => a.value === b.value}
      filter={
        props.creatable
          ? (item: Option, query: string) => {
              if (item.value.startsWith(CREATE_PREFIX)) return true
              if (!query) return true
              return item.label.toLowerCase().includes(query.toLowerCase())
            }
          : undefined
      }
    >
      <ComboboxTrigger
        render={
          <Button
            id={props.id}
            type="button"
            variant="outline"
            aria-invalid={props.isError || undefined}
            className={cn(
              'h-auto min-h-8 w-full items-center justify-between font-normal',
              props.value.filter(Boolean).length > 0 ? 'pl-1.5 py-1' : 'text-muted-foreground',
              props.className,
            )}
          />
        }
      >
        <ComboboxValue>
          {props.value.filter(Boolean).length > 0 ? (
            <span className="flex flex-wrap items-center gap-1">
              {props.value.filter(Boolean).map((v) => (
                <Badge
                  key={v}
                  variant="secondary"
                  className="bg-foreground/8 text-foreground dark:bg-foreground/15 font-normal"
                >
                  {labelByValue.get(v) ?? v}
                  <span
                    role="button"
                    tabIndex={-1}
                    className="-mr-0.5 ml-0.5 inline-flex cursor-pointer items-center rounded-sm opacity-50 hover:opacity-100"
                    onPointerDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      props.onChange(
                        props.value.filter((val) => val !== v),
                        undefined,
                      )
                    }}
                  >
                    <XIcon className="size-3" />
                  </span>
                </Badge>
              ))}
            </span>
          ) : (
            <span>{props.placeholder}</span>
          )}
        </ComboboxValue>
      </ComboboxTrigger>
      <ComboboxContent className="min-w-(--anchor-width)">
        <ComboboxInput showTrigger={false} placeholder={props.searchPlaceholder} />
        <ComboboxEmpty>{props.commandEmpty}</ComboboxEmpty>
        <ComboboxList>
          {(item: Option) => (
            <ComboboxItem key={item.value} value={item}>
              {item.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  )
}
